import { useState, type ReactNode } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import type {
  LexCounselDashboardDraft,
  LexCounselDashboardHearing,
  LexCounselDashboardReminder,
} from '../../lib/lexCounselDashboardBrief';
import { useLexCounselStore } from '../../store/lexCounselStore';
import {
  LexCounselGradientName,
  PANEL_BODY,
  PANEL_CAPTION,
  PANEL_DISPLAY,
  PANEL_GREETING,
  PANEL_MUTED,
} from './lexCounselPanelUi';

type Props = {
  name: string;
};

type StatId = 'hearings' | 'reminders' | 'drafts';

function formatHearingWhen(daysUntil: number, date: string): string {
  if (daysUntil === 0) return 'Today';
  if (daysUntil === 1) return 'Tomorrow';
  try {
    const d = parseISO(date);
    if (isValid(d)) return format(d, 'EEE, d MMM');
  } catch {
    /* ignore */
  }
  return `In ${daysUntil} days`;
}

function sendToLexCounsel(prompt: string) {
  window.dispatchEvent(new CustomEvent('lexcounsel-send', { detail: { text: prompt } }));
}

function DetailButton({
  title,
  meta,
  onClick,
}: {
  title: string;
  meta?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[var(--counsel-hover)] ${PANEL_BODY}`}
    >
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {meta ? <span className={`shrink-0 ${PANEL_CAPTION}`}>{meta}</span> : null}
    </button>
  );
}

function AskCounselLink({ prompt }: { prompt: string }) {
  return (
    <button
      type="button"
      onClick={() => sendToLexCounsel(prompt)}
      className={`mt-1 px-3 py-1.5 text-left text-[var(--counsel-accent)] transition-colors hover:text-[var(--counsel-accent-hover)] ${PANEL_CAPTION}`}
    >
      Ask Lex Counsel about this
    </button>
  );
}

function InteractiveStatRow({
  id,
  expanded,
  onToggle,
  value,
  label,
  note,
  children,
}: {
  id: StatId;
  expanded: boolean;
  onToggle: (id: StatId) => void;
  value: number;
  label: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <li className="overflow-hidden rounded-xl border border-transparent transition-colors hover:border-[var(--counsel-border-subtle)] hover:bg-[var(--counsel-hover-subtle)]">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={expanded}
        className="flex w-full items-baseline gap-3 px-2 py-2 text-left"
      >
        <span className="w-6 shrink-0 tabular-nums text-[15px] font-semibold text-[var(--counsel-text)]">
          {value}
        </span>
        <span className={`min-w-0 flex-1 ${PANEL_BODY}`}>
          {label}
          {note ? <span className={`ml-1.5 ${PANEL_MUTED}`}>{note}</span> : null}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--counsel-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-[var(--counsel-border-subtle)] px-1 pb-2 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

function ScrollableDetailList({ children }: { children: ReactNode }) {
  return (
    <div className="max-h-[min(200px,40vh)] overflow-y-auto overscroll-contain pr-0.5 custom-scrollbar">
      {children}
    </div>
  );
}

function HearingsDetail({
  today,
  upcoming,
  navigate,
}: {
  today: LexCounselDashboardHearing[];
  upcoming: LexCounselDashboardHearing[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  if (!today.length && !upcoming.length) {
    return (
      <div className="px-2 py-1">
        <p className={PANEL_MUTED}>No hearings in the next 14 days.</p>
        <AskCounselLink prompt="What hearings are coming up across my active matters?" />
      </div>
    );
  }

  return (
    <ScrollableDetailList>
      <div className="space-y-1">
        {today.map((h) => (
          <DetailButton
            key={`today-${h.caseId}`}
            title={h.title}
            meta="Today"
            onClick={() => navigate(`/cases/${h.caseId}`)}
          />
        ))}
        {upcoming.map((h) => (
          <DetailButton
            key={`upcoming-${h.caseId}-${h.date}`}
            title={h.title}
            meta={formatHearingWhen(h.daysUntil, h.date)}
            onClick={() => navigate(`/cases/${h.caseId}`)}
          />
        ))}
        <AskCounselLink prompt="Summarize hearings coming up across my active matters this week." />
      </div>
    </ScrollableDetailList>
  );
}

function RemindersDetail({
  reminders,
  navigate,
}: {
  reminders: LexCounselDashboardReminder[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  if (!reminders.length) {
    return (
      <div className="px-2 py-1">
        <p className={PANEL_MUTED}>No pending action items right now.</p>
      </div>
    );
  }

  return (
    <ScrollableDetailList>
      <div className="space-y-1">
        {reminders.map((r) => (
          <DetailButton
            key={r.id}
            title={r.caseTitle ? `${r.message} · ${r.caseTitle}` : r.message}
            onClick={() => {
              if (r.caseId) navigate(`/cases/${r.caseId}`);
            }}
          />
        ))}
        <AskCounselLink prompt="What pending reminders and action items need my attention today?" />
      </div>
    </ScrollableDetailList>
  );
}

function DraftsDetail({
  drafts,
  navigate,
}: {
  drafts: LexCounselDashboardDraft[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  if (!drafts.length) {
    return (
      <div className="px-2 py-1">
        <p className={PANEL_MUTED}>No drafts in progress.</p>
        <AskCounselLink prompt="Help me start a new legal document." />
      </div>
    );
  }

  return (
    <ScrollableDetailList>
      <div className="space-y-1">
        {drafts.map((d) => (
          <DetailButton
            key={d.draftId}
            title={d.title}
            meta={d.caseTitle}
            onClick={() => navigate(`/drafts/${d.draftId}`)}
          />
        ))}
        <AskCounselLink prompt="Which drafts need my attention, and what should I work on next?" />
      </div>
    </ScrollableDetailList>
  );
}

/**
 * Dashboard empty-state brief inside the Lex Counsel panel.
 */
export default function LexCounselPanelDailyBrief({ name }: Props) {
  const navigate = useNavigate();
  const brief = useLexCounselStore((s) => s.workspace.dashboardBrief);
  const [expanded, setExpanded] = useState<StatId | null>(null);

  const todayHearings = brief?.hearings.filter((h) => h.daysUntil === 0) ?? [];
  const upcoming = brief?.hearings.filter((h) => h.daysUntil > 0 && h.daysUntil <= 7) ?? [];
  const pendingReminders = brief?.pendingReminders ?? 0;
  const openDrafts = brief?.openDrafts ?? 0;
  const reminders = brief?.reminders ?? [];
  const draftItems = brief?.openDraftItems ?? [];

  const toggle = (id: StatId) => {
    setExpanded((current) => (current === id ? null : id));
  };

  return (
    <div className="mb-6">
      <p className={PANEL_GREETING}>
        Hello, <LexCounselGradientName>{name}</LexCounselGradientName>
      </p>
      <h2 className={`mt-2 ${PANEL_DISPLAY}`}>How can I help you today?</h2>

      {brief ? (
        <div className="mt-6 rounded-2xl border border-[var(--counsel-card-border)] bg-[var(--counsel-card-bg)] px-3 py-3.5">
          <p className={`mb-3 px-1 ${PANEL_MUTED}`}>Your workspace</p>
          <ul className="space-y-1.5">
            <InteractiveStatRow
              id="hearings"
              expanded={expanded === 'hearings'}
              onToggle={toggle}
              value={todayHearings.length}
              label={`hearing${todayHearings.length === 1 ? '' : 's'} today`}
              note={upcoming.length > 0 ? `· ${upcoming.length} this week` : undefined}
            >
              <HearingsDetail today={todayHearings} upcoming={upcoming} navigate={navigate} />
            </InteractiveStatRow>
            <InteractiveStatRow
              id="reminders"
              expanded={expanded === 'reminders'}
              onToggle={toggle}
              value={pendingReminders}
              label={`pending reminder${pendingReminders === 1 ? '' : 's'}`}
            >
              <RemindersDetail reminders={reminders} navigate={navigate} />
            </InteractiveStatRow>
            <InteractiveStatRow
              id="drafts"
              expanded={expanded === 'drafts'}
              onToggle={toggle}
              value={openDrafts}
              label={`draft${openDrafts === 1 ? '' : 's'} in progress`}
            >
              <DraftsDetail drafts={draftItems} navigate={navigate} />
            </InteractiveStatRow>
          </ul>
        </div>
      ) : (
        <p className={`mt-5 ${PANEL_MUTED}`}>Loading your workspace…</p>
      )}
    </div>
  );
}
